// @flow

import React, { type Node } from 'react';
import { useIncomingPayment, getReceipts, decideReceipt } from '@tectonics/prs/incoming-payment';
import { type Amount, amountToCurrencyString } from '@tectonics/prs/money';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { type CheckoutReceipts, type Receipt, getInstalmentByNumber } from '@tectonics/prs/receipts';
import { getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { getMoneyFormat } from '../commons/money-format';

export function TotalValue(): Node {
  const incomingPayment = useIncomingPayment();

  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);

  if (incomingPayment.status === 'OP') {
    const receipts = getReceipts(incomingPayment);
    return <OpenedTotalValue receipts={receipts} formatAmount={formatAmount} />;
  }

  const receipt = decideReceipt(incomingPayment);
  return <GenericTotalValue receipt={receipt} formatAmount={formatAmount} />;
}

type OpenedTotalValueProps = {|
  receipts: CheckoutReceipts,
  formatAmount: (Amount) => string,
|};

function OpenedTotalValue({ receipts, formatAmount }: OpenedTotalValueProps): Node {
  const [selectedInstalment] = useUserInputValue('selectedInstalment');
  const instalment = getInstalmentByNumber(receipts, Number(selectedInstalment));

  if (!instalment)
    throw new Error(`There is instalment with "${selectedInstalment}"`);

  return formatAmount(getReceiptAmountLocalTotal(instalment));
}

type GenericTotalValueProps = {|
  receipt: Receipt,
  formatAmount: (Amount) => string,
|};

function GenericTotalValue({ receipt, formatAmount }: GenericTotalValueProps): Node {
  return formatAmount(getReceiptAmountLocalTotal(receipt));
}
